import { memo, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';

export type SettingsIntegrationRowChevron = boolean | 'expand' | 'navigate';

export interface SettingsIntegrationRowProps {
  readonly mark?: ReactNode;
  readonly icon?: LucideIcon;
  readonly title: string;
  readonly subtitle?: string;
  /** Optional secondary meta under the subtitle (e.g. quiet config summary). */
  readonly meta?: ReactNode;
  readonly status?: ReactNode;
  readonly onClick?: () => void;
  /** In-place expand → ChevronDown; open/navigate (e.g. modal) → ChevronRight. */
  readonly chevron?: SettingsIntegrationRowChevron;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly 'aria-label'?: string;
  /** Prefer `dialog` when onClick opens a modal (not an in-place expand). */
  readonly 'aria-haspopup'?: boolean | 'dialog';
  readonly 'data-testid'?: string;
}

function resolveChevronIcon(
  chevron: SettingsIntegrationRowChevron | undefined,
): LucideIcon | null {
  if (chevron === false || chevron == null) {
    return null;
  }
  if (chevron === 'expand') {
    return ChevronDown;
  }
  return ChevronRight;
}

export const SettingsIntegrationRow = memo(
  ({
    mark,
    icon: Icon,
    title,
    subtitle,
    meta,
    status,
    onClick,
    chevron,
    className = '',
    disabled = false,
    'aria-label': ariaLabel,
    'aria-haspopup': ariaHasPopup,
    'data-testid': dataTestId,
  }: SettingsIntegrationRowProps): JSX.Element => {
    const ChevronIcon = resolveChevronIcon(chevron);
    const showChevron = ChevronIcon != null && !disabled;
    const interactive = onClick != null && !disabled;
    const alignStart = meta != null;
    const rootClass = [
      'flex w-full gap-3 rounded-xl border border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-3 py-3 text-left shadow-[var(--elevation-1)]',
      alignStart ? 'items-start' : 'items-center',
      interactive
        ? 'cursor-pointer transition-[border-color,box-shadow,background-color] duration-150 hover:border-[color-mix(in_srgb,var(--color-an-primary)_35%,var(--color-an-border))] hover:bg-[color-mix(in_srgb,var(--color-an-primary)_4%,var(--color-an-surface))] hover:shadow-[var(--elevation-2)] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[color-mix(in_srgb,var(--color-an-primary)_45%,transparent)]'
        : '',
      disabled ? 'cursor-not-allowed opacity-60' : '',
      className,
    ]
      .filter((part) => part.length > 0)
      .join(' ');

    let markNode: ReactNode = null;
    if (mark != null) {
      markNode = mark;
    } else if (Icon != null) {
      markNode = (
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--color-an-border)] bg-[var(--color-an-surface-muted)] text-[var(--color-an-text)]"
          aria-hidden="true"
        >
          <Icon className="h-4 w-4 stroke-[1.75]" />
        </span>
      );
    }

    const trailingAlign = alignStart ? 'mt-1.5' : '';

    const body = (
      <>
        {markNode}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-snug text-[var(--color-an-text)]">
            {title}
          </span>
          {subtitle != null && subtitle !== '' ? (
            <span className="mt-0.5 block text-xs leading-normal text-[var(--color-an-text-muted)]">
              {subtitle}
            </span>
          ) : null}
          {meta != null ? <span className="mt-1.5 block min-w-0">{meta}</span> : null}
        </span>
        {status != null ? (
          <span className={`shrink-0 ${trailingAlign}`.trim()}>{status}</span>
        ) : null}
        {showChevron && ChevronIcon != null ? (
          <ChevronIcon
            className={`h-4 w-4 shrink-0 text-[var(--color-an-text-muted)] stroke-[1.75] ${trailingAlign}`.trim()}
            aria-hidden="true"
            data-testid={
              chevron === 'expand'
                ? 'settings-integration-row-chevron-expand'
                : 'settings-integration-row-chevron-navigate'
            }
          />
        ) : null}
      </>
    );

    if (onClick != null) {
      return (
        <button
          type="button"
          className={rootClass}
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          aria-disabled={disabled ? true : undefined}
          aria-label={ariaLabel}
          aria-haspopup={ariaHasPopup}
          data-testid={dataTestId}
        >
          {body}
        </button>
      );
    }

    return (
      <div className={rootClass} data-testid={dataTestId}>
        {body}
      </div>
    );
  },
);

SettingsIntegrationRow.displayName = 'SettingsIntegrationRow';
